import { IAssistantAction, TourConfig } from '../..';
import * as styles from './blinkingHighlighter.scss';

const defaultColor = {
  r: 255,
  g: 0,
  b: 0,
};

class BlinkingHighLighter {
  originalStyle: Partial<CSSStyleDeclaration> = {};
  blinkingHighlightColor: TourConfig['blinkingHighlightColor'];

  highlightElementByAction(
    action: IAssistantAction,
    Color?: TourConfig['blinkingHighlightColor'],
  ): Function {
    this.blinkingHighlightColor =
      action.customBlinkingHighlightColor || Color || defaultColor;
    const unhighlightCallbacks = [];

    if (action.blinkingHighlightElements) {
      const unhighlightElementsCallbacks = action.blinkingHighlightElements.map(
        (element) =>
          this.highlightElement(
            document.querySelector<HTMLElement>(element),
            action.customStyle?.padding,
          ),
      );
      unhighlightCallbacks.push(...unhighlightElementsCallbacks);
    }
    return function () {
      unhighlightCallbacks.forEach((cb) => cb());
    };
  }

  private setElementPadding(element: HTMLElement, padding: string) {
    element.style.padding = padding;
    element.style.boxSizing = 'content-box';
  }

  private getAnimationColor() {
    return Object.keys(this.blinkingHighlightColor)
      .map((c) => this.blinkingHighlightColor[c])
      .join(',');
  }

  private setAnimationColor(element: HTMLElement) {
    const animationColor = this.getAnimationColor();
    element.style.setProperty(`--animation-color`, `${animationColor}`);
  }

  private createBlinkingElement(
    element: HTMLElement,
    padding?: string | number,
  ) {
    const blinkingElement = document.createElement('blinkingElement');
    blinkingElement.classList.add(styles.TourBlinkingHighlight);
    blinkingElement.style.width = getComputedStyle(element).width;
    blinkingElement.style.height = getComputedStyle(element).height;
    this.setAnimationColor(blinkingElement);
    padding && this.setElementPadding(blinkingElement, `${padding}`);
    return blinkingElement;
  }

  private addBlinkingElement(
    element: HTMLElement,
    blinkingElement: HTMLElement,
  ) {
    element.insertBefore(blinkingElement, element.firstChild);
    this.originalStyle.position = element.style.position;
    this.originalStyle.overflow = element.style.overflow;
    if (window.getComputedStyle(element).position === 'static') {
      element.style.position = 'relative';
    }
    element.style.overflow = 'visible';
  }

  private restoreElement(element: HTMLElement, child: HTMLElement) {
    element.removeChild(child);
    element.style.position = this.originalStyle.position;
    element.style.overflow = this.originalStyle.overflow;
  }

  private highlightElement(element: HTMLElement, padding?: string | number) {
    if (!element) {
      return () => {};
    }
    const blinkingElement = this.createBlinkingElement(element, padding);
    this.addBlinkingElement(element, blinkingElement);

    const that = this;

    return (function (_element, _blinkingElement) {
      return function () {
        that.unhighlighElement(_element, _blinkingElement);
      };
    })(element, blinkingElement);
  }

  private unhighlighElement(element: HTMLElement, child: HTMLElement) {
    element.contains(child) && this.restoreElement(element, child);
  }
}

export default new BlinkingHighLighter();
